import type { AxiosRequestConfig } from "axios";
import {
  AuthResponse,
  LoginCredentials,
  User,
  LoginResponse,
  AccountStatus,
  MUST_COMPLETE_RESET_STATUSES,
  MeApiResponse,
  RefreshSessionResponse,
  SaveTempUserRequest,
  TempUserResponse,
  SendOtpRequest,
  VerifyOtpRequest,
  OtpResponse,

  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  UpdatePasswordRequest,
  RecoverUserIdRequest,
  RecoverUserIdResponse,
  UpdateUserRequest,
  UpdateUserResponse,
} from "../types";

/** Response from GET /auth/session (lightweight session check) */
export interface SessionCheckResponse {
  authenticated: true;
  status: string;
}
import apiService from "@/services/api";

// ============================================
// Public APIs (no auth required)
// ============================================

/** Country/nationality option from GET /public/countries (value = id, label = name) */
export interface CountryOption {
  value: number;
  label: string;
}

/** Passport type option from GET /public/passport-types (value = id, label = name) */
export interface PassportTypeOption {
  value: number;
  label: string;
}

/** Signup configuration from GET /public/signup-config (backend is source of truth). */
export interface SignupConfig {
  signupMinAge: number;
  passportMinValidityDays: number;
  passportMaxValidityYears: number;
  /**
   * Country-specific privacy policy / data protection URL resolved by the
   * backend from the request origin. `null` when not configured for the
   * country – callers should hide any "click here" links in that case.
   */
  privacyPolicyUrl: string | null;
}

/** Writing direction for a UI language (drives `<html dir>`). */
export type LanguageDirection = 'ltr' | 'rtl';

/** Supported language from GET /public/supported-languages (backend is source of truth). */
export interface SupportedLanguageOption {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  /** When true, this language is selected by default in the selector. */
  isDefault: boolean;
  /** Writing direction. Used to set `document.documentElement.dir`. */
  dir: LanguageDirection;
}

/**
 * Get supported UI languages from backend (single source of truth).
 * GET /public/supported-languages
 */
const getSupportedLanguages = async (): Promise<SupportedLanguageOption[]> => {
  const response = await apiService.get<SupportedLanguageOption[]>("/public/supported-languages");
  return response.data;
};

/**
 * Get countries (nationalities) as value-label options for dropdowns.
 * GET /v1/public/countries
 */
const getCountries = async (): Promise<CountryOption[]> => {
  const response = await apiService.get<CountryOption[]>("/public/countries");
  return response.data;
};

/**
 * Get passport types as value-label options for dropdowns.
 * GET /v1/public/passport-types
 */
const getPassportTypes = async (): Promise<PassportTypeOption[]> => {
  const response = await apiService.get<PassportTypeOption[]>("/public/passport-types");
  return response.data;
};

/**
 * Get signup-related configuration (min age, passport validity days).
 * GET /v1/public/signup-config
 */
const getSignupConfig = async (): Promise<SignupConfig> => {
  const response = await apiService.get<SignupConfig>("/public/signup-config");
  return response.data;
};

// ============================================
// Public Track Status API
// ============================================

export type TrackStatusReferenceType = "appointment" | "application";

export interface TrackStatusAppointmentParticipant {
  participantName: string;
  appointmentDateTime: string;
  appointmentStatus: string;
}

export interface TrackStatusAppointmentResponse {
  participants: TrackStatusAppointmentParticipant[];
}

export interface TrackStatusApplicationResponse {
  status: string;
}

export type TrackStatusResponse =
  | TrackStatusAppointmentResponse
  | TrackStatusApplicationResponse;

export function isAppointmentTrackResult(
  data: TrackStatusResponse,
): data is TrackStatusAppointmentResponse {
  return "participants" in data;
}

export interface PublicTrackStatusParams {
  referenceType: TrackStatusReferenceType;
  referenceNumber: string;
  passportNumber: string;
}

/**
 * GET /public/track-status
 * Public (unauthenticated) status lookup by reference + passport.
 */
const getPublicTrackStatus = async (
  params: PublicTrackStatusParams,
): Promise<TrackStatusResponse> => {
  const response = await apiService.get<TrackStatusResponse>(
    "/public/track-status",
    { params },
  );
  return response.data;
};

// ============================================
// Authentication APIs
// ============================================

/**
 * Login user
 * POST /users/login
 * @param config - Optional Axios config (e.g. reCAPTCHA headers via `recaptchaHeaders(token)`).
 */
const login = async (creds: LoginCredentials, config?: AxiosRequestConfig): Promise<AuthResponse> => {
  const response = await apiService.post<LoginResponse>("/users/login", creds, config);
  // Login only returns minimal data - construct partial User
  // Full user data will be fetched via getSession after login
  const user: User = {
    id: response.data.id,
    email: creds.email, // Use email from credentials since BE doesn't return it
    mobile: null,
    surName: null,
    firstName: null,
    lastName: null,
    middleName: null,
    dob: null,
    passportTypeId: null,
    passportIssueCountryId: null,
    passportNumber: null,
    passportIssueDate: null,
    passportExpiryDate: null,
    passportIssuePlace: null,
    role: "USER", // Default role for booking portal
    status: response.data.status,
    languagePreference: null,
    isMobileOtpVerified: false,
    isEmailOtpVerified: false,
    createdAt: new Date(),
    updatedAt: new Date(),
    isActive: true,
  };

  const forceChangePassword = MUST_COMPLETE_RESET_STATUSES.includes(response.data.status as AccountStatus);

  const result = {
    user,
    forceChangePassword,
  };
  return result;
};

/**
 * Lightweight session check – use on every load.
 * GET /auth/session
 * Returns authenticated + status; use /users/me only on protected routes for full user.
 */
const getSessionCheck = async (): Promise<{ authenticated: true; status: AccountStatus } | null> => {
  try {
    const response = await apiService.get<SessionCheckResponse>("/auth/session");
    const data = response.data;
    if (!data || !data.authenticated || !data.status) return null;
    return {
      authenticated: true,
      status: data.status as AccountStatus,
    };
  } catch (error: any) {
    if (error.status === 401) return null;
    if (!error.status || error.status === 0 || error.message?.includes('ERR_CONNECTION_REFUSED')) return null;
    throw error;
  }
};

/**
 * Get current user session (full user + session metadata).
 * GET /users/me – use only on protected routes.
 */
const getSession = async (): Promise<User | null> => {
  try {
    const response = await apiService.get<User | MeApiResponse>("/users/me");
    const data = response.data;
    if (!data) return null;
    // New API shape: { user, sessionMetadata } at top level
    if ('sessionMetadata' in data && data.sessionMetadata) {
      return { ...data.user, session: data.sessionMetadata } as User;
    }
    // Legacy shape: response is User with session already nested
    return data as User;
  } catch (error: any) {
    if (error.status === 401) {
      return null;
    }
    if (!error.status || error.status === 0 || error.message?.includes('ERR_CONNECTION_REFUSED')) {
      return null;
    }
    throw error;
  }
};

/**
 * Logout user
 * POST /users/logout
 */
const logout = async (): Promise<void> => {
  await apiService.post("/users/logout");
};

/**
 * Refresh session to extend idle timeout
 * POST /users/refresh-session
 */
const refreshSession = async (): Promise<RefreshSessionResponse> => {
  const response = await apiService.post<RefreshSessionResponse>("/users/refresh-session");
  return response.data;
};

// ============================================
// Signup Flow APIs
// ============================================

/**
 * Save temporary user data before email verification
 * POST /users/save-temp
 * @param config - Optional Axios config (e.g. reCAPTCHA headers via `recaptchaHeaders(token)`).
 */
const saveTempUser = async (data: SaveTempUserRequest, config?: AxiosRequestConfig): Promise<TempUserResponse> => {
  const response = await apiService.post<TempUserResponse>("/users/save-temp", data, config);
  return response.data;
};

/**
 * Send OTP to email
 * POST /otp/send
 * @param config - Optional Axios config (e.g. reCAPTCHA headers via `recaptchaHeaders(token)`).
 */
const sendOtp = async (data: SendOtpRequest, config?: AxiosRequestConfig): Promise<OtpResponse> => {
  const response = await apiService.post<OtpResponse>("/otp/send", data, config);
  return response.data;
};

/**
 * Verify OTP
 * POST /otp/verify
 * @param config - Optional Axios config (e.g. reCAPTCHA headers via `recaptchaHeaders(token)`).
 */
const verifyOtp = async (data: VerifyOtpRequest, config?: AxiosRequestConfig): Promise<OtpResponse> => {
  const response = await apiService.post<OtpResponse>("/otp/verify", data, config);
  return response.data;
};



// ============================================
// Password Reset Flow APIs
// ============================================

/**
 * Initiate forgot password flow (creates session with 10min expiry)
 * POST /users/forgot-password
 * Prerequisites: Email OTP must be verified first
 * @param config - Optional Axios config (e.g. reCAPTCHA headers via `recaptchaHeaders(token)`).
 */
const forgotPassword = async (data: ForgotPasswordRequest, config?: AxiosRequestConfig): Promise<ForgotPasswordResponse> => {
  const response = await apiService.post<ForgotPasswordResponse>("/users/forgot-password", data, config);
  return response.data;
};

/**
 * Reset password after forgot password flow
 * POST /users/reset-password
 * Prerequisites: Must have active session from forgotPassword
 */
const resetPassword = async (data: ResetPasswordRequest): Promise<void> => {
  await apiService.post("/users/reset-password", data);
};

/**
 * Update password (for first login or authenticated password change)
 * POST /users/update-password
 */
const updatePassword = async (data: UpdatePasswordRequest): Promise<void> => {
  await apiService.post("/users/update-password", data);
};



// ============================================
// Legacy method (kept for backward compatibility)
// ============================================

interface ChangePasswordData {
  email: string;
  currentPassword: string;
  newPassword: string;
}

const changePassword = async (data: ChangePasswordData): Promise<{ id: number; email: string; status: string }> => {
  const response = await apiService.post<{ id: number; email: string; status: string }>(
    "/users/reset-password",
    {
      email: data.email,
      password: data.currentPassword,
      newPassword: data.newPassword,
    }
  );
  return response.data;
};

/**
 * Update user profile
 * PUT /users
 */
const updateUser = async (data: UpdateUserRequest): Promise<UpdateUserResponse> => {
  const response = await apiService.put<UpdateUserResponse>("/users", data);
  return response.data;
};

/**
 * Update user language preference (persisted across sessions).
 * PATCH /users/language-preference
 */
const updateLanguagePreference = async (languagePreference: string): Promise<void> => {
  await apiService.patch("/users/language-preference", { languagePreference });
};

/**
 * Delete user account (soft delete)
 * DELETE /users
 */
const deleteAccount = async (): Promise<void> => {
  await apiService.delete("/users");
};

/**
 * Recover user ID
 * POST /users/recover-user-id
 * @param config - Optional Axios config (e.g. reCAPTCHA headers via `recaptchaHeaders(token)`).
 */
const recoverUserId = async (data: RecoverUserIdRequest, config?: AxiosRequestConfig): Promise<RecoverUserIdResponse> => {
  const response = await apiService.post<RecoverUserIdResponse>("/users/recover-user-id", data, config);
  return response.data;
};

export const authService = {
  // Public
  getCountries,
  getPassportTypes,
  getSignupConfig,
  getSupportedLanguages,
  getPublicTrackStatus,
  // Auth
  getSessionCheck,
  login,
  getSession,
  logout,
  refreshSession,
  // Signup
  saveTempUser,
  sendOtp,
  verifyOtp,

  // Password Reset
  forgotPassword,
  resetPassword,
  updatePassword,

  // Account
  updateUser,
  updateLanguagePreference,
  deleteAccount,
  recoverUserId,
  // Legacy
  changePassword,
};

